'use client';

import { useState, useMemo } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import Modal from '@/shared/components/overlay/modal';
import { Input } from '@/shared/components/primitives/input';
import { useOverlayStore } from '@/shared/components/overlay/overlay.store';
import { toggleFilterInParams } from '@/features/games/internal/helpers/filter-url-helpers';
import type { FilterOption } from '@/features/games/internal/types/category-filters';
import { GameCardUIStar } from '@/features/games/shared/components/game-card-ui-star';
import { useTranslations } from 'next-intl';

type FilterModalProps = {
  modalKey: string;
  title: string;
  filterType: string;
  options: FilterOption[];
};

export const FilterModal = ({ modalKey, title, filterType, options }: FilterModalProps) => {
  const t = useTranslations('Categories');
  const [searchTerm, setSearchTerm] = useState('');
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { hideOverlay } = useOverlayStore();
  const isRatingFilter = filterType === 'ratingMin';

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;

    try {
      const regex = new RegExp(searchTerm.trim(), 'i');
      return options.filter((option) => regex.test(option.label));
    } catch {
      const lowerSearch = searchTerm.toLowerCase();
      return options.filter((option) => option.label.toLowerCase().includes(lowerSearch));
    }
  }, [options, searchTerm]);

  const handleOptionClick = (value: string) => {
    const currentParams = Object.fromEntries(searchParams?.entries() ?? []);
    const newParams = toggleFilterInParams(currentParams, filterType, value);
    const newUrl = `${pathname}?${newParams.toString()}`;
    router.replace(newUrl, { scroll: false });
    hideOverlay();
  };

  return (
    <Modal modalKey={modalKey} header={<h2 className='text-2xl font-bold'>{title}</h2>}>
      <div className='p-6'>
        <Input
          type='text'
          placeholder='Search'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className='mb-6'
        />

        <div
          className='grid grid-cols-3 gap-x-8 gap-y-2 overflow-y-auto'
          style={{ maxHeight: '60vh' }}
        >
          {filteredOptions.map((option) => {
            const ratingValue = Number(option.value);
            return (
              <button
                key={option.value}
                onClick={() => handleOptionClick(option.value)}
                className='text-left'
              >
                <span className='text-text hover:text-text-hover flex items-center gap-1 text-base'>
                  {isRatingFilter ? (
                    <>
                      {Array.from({ length: 5 }, (_, index) => {
                        const starIndex = index + 1;
                        const fillPct = starIndex <= ratingValue ? 100 : 0;
                        return (
                          <GameCardUIStar
                            key={starIndex}
                            fillPct={fillPct}
                            size={16}
                            className='inline-block'
                          />
                        );
                      })}
                      <span className='ml-1'>& up</span>
                    </>
                  ) : (
                    option.label
                  )}
                  <span className='text-text-muted'>({option.count})</span>
                </span>
              </button>
            );
          })}
        </div>

        {filteredOptions.length === 0 && (
          <div className='text-text-muted py-8 text-center'>{t('noResultsFound')}</div>
        )}
      </div>
    </Modal>
  );
};
